import * as Sentry from '@sentry/react';

type ErrorSeverity = 'fatal' | 'error' | 'warning' | 'info' | 'debug';

interface CaptureErrorOptions {
  /** Additional context tags for filtering in Sentry */
  tags?: Record<string, string>;
  /** Extra data to attach to the error */
  extra?: Record<string, unknown>;
  /** Error severity level */
  level?: ErrorSeverity;
  /** User information */
  user?: {
    id?: string;
    email?: string;
    username?: string;
  };
}

interface CaptureMessageOptions extends Omit<CaptureErrorOptions, 'level'> {
  level?: ErrorSeverity;
}

/**
 * Sentry utility for manual error and message capturing
 *
 * @example
 * // Capture an error with context
 * sentry.captureError(error, {
 *   tags: { component: 'ChatInput' },
 *   extra: { messageId: '123' }
 * });
 *
 * @example
 * // Capture a message/log
 * sentry.captureMessage('User performed action', {
 *   level: 'info',
 *   tags: { action: 'export' }
 * });
 *
 * @example
 * // Wrap async function with error capture
 * const result = await sentry.withErrorCapture(
 *   () => fetchData(),
 *   { tags: { api: 'assistants' } }
 * );
 */
export const sentry = {
  /**
   * Capture an error and send to Sentry
   */
  captureError(
    error: Error | unknown,
    options: CaptureErrorOptions = {}
  ): string | undefined {
    const { tags, extra, level = 'error', user } = options;

    return Sentry.captureException(error, scope => {
      if (tags) {
        Object.entries(tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      if (extra) {
        Object.entries(extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      if (user) {
        scope.setUser(user);
      }

      scope.setLevel(level);

      return scope;
    });
  },

  /**
   * Capture a message/log and send to Sentry
   */
  captureMessage(
    message: string,
    options: CaptureMessageOptions = {}
  ): string | undefined {
    const { tags, extra, level = 'info', user } = options;

    return Sentry.captureMessage(message, scope => {
      if (tags) {
        Object.entries(tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      if (extra) {
        Object.entries(extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      if (user) {
        scope.setUser(user);
      }

      scope.setLevel(level);

      return scope;
    });
  },

  /**
   * Set user context for all future events
   */
  setUser(user: { id?: string; email?: string; username?: string } | null) {
    Sentry.setUser(user);
  },

  /**
   * Set a global tag for all future events
   */
  setTag(key: string, value: string) {
    Sentry.setTag(key, value);
  },

  /**
   * Set global extra data for all future events
   */
  setExtra(key: string, value: unknown) {
    Sentry.setExtra(key, value);
  },

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb(breadcrumb: {
    category?: string;
    message?: string;
    level?: ErrorSeverity;
    data?: Record<string, unknown>;
  }) {
    Sentry.addBreadcrumb(breadcrumb);
  },

  /**
   * Wrap an async function and automatically capture any errors
   * Returns the result or undefined if an error occurred
   */
  async withErrorCapture<T>(
    fn: () => Promise<T>,
    options: CaptureErrorOptions = {}
  ): Promise<T | undefined> {
    try {
      return await fn();
    } catch (error) {
      this.captureError(error, options);
      return undefined;
    }
  },

  /**
   * Wrap an async function and automatically capture errors
   * Re-throws the error after capturing
   */
  async withErrorCaptureRethrow<T>(
    fn: () => Promise<T>,
    options: CaptureErrorOptions = {}
  ): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      this.captureError(error, options);
      throw error;
    }
  },

  /**
   * Start a performance transaction
   */
  startTransaction(name: string, op: string) {
    return Sentry.startInactiveSpan({ name, op });
  },

  /**
   * Create a scoped context for a block of code
   * Useful for adding context to multiple operations
   */
  withScope<T>(callback: (scope: Sentry.Scope) => T): T {
    return Sentry.withScope(callback);
  },
};

// Export types for consumers
export type { CaptureErrorOptions, CaptureMessageOptions, ErrorSeverity };
